import { Fragment, useEffect, useState } from "react";
import {
    Box, Grid, FormControl, InputLabel, Select, Button, Card, CardActions,
    CardContent, CardMedia, Container, Divider, Backdrop, CircularProgress,
    Typography, FormControlLabel, FormLabel, Radio, RadioGroup,
    createTheme, ThemeProvider
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CheckAttr from "../../../../utils/CheckAttr";
import { DataRef } from "../../../../types/DataRef";
import { AlertType } from "../../../../types/AlertType";
import { SnackBarAlert } from "../../../element/SnackBarAlert";
import { downloadFile } from "../../../../utils/utils";
import { MODE_BUDGET } from "../../../../constants/Constants";
import Cookies from "universal-cookie";
import axios, { AxiosInstance } from "axios";
import Input from "./element/Input";
import VsElement from "./element/VsElement";

const theme = createTheme({
    components: {
        MuiFormControlLabel: {
            styleOverrides: {
                label: {
                    fontSize: '0.8rem',
                    color: 'rgba(0, 0, 0, 0.6)'
                },
            },
        },
    },
});

export interface ExtractData {
    yearProd: Dayjs | null;
    yearBudget: Dayjs | null;
    yearReforecast: Dayjs | null;
    consolidation?: "Annuelle" | "Mensuelle";
    pays?: number | "";
    bu?: number;
    client?: string;
    operation?: string;
}

interface DataRefClientWithBudget {
    id: number;
    libelle: string;
    is_clientBudget: number;
}

interface DataRefOperationWithBudget {
    id: number;
    libelle: string;
    is_operationBudget: number;
}

type EXTRACTION_TYPE = "REALISER_VS_BUDGET_VS_REFORECAST" | "REALISER_VS_REFORECAST" | "BUDGET_VS_REFORECAST";

enum ExtractionType {
    REALISER_VS_BUDGET_VS_REFORECAST = "REALISER_VS_BUDGET_VS_REFORECAST",
    REALISER_VS_REFORECAST = "REALISER_VS_REFORECAST",
    BUDGET_VS_REFORECAST = "BUDGET_VS_REFORECAST"
};

const initialData: ExtractData = {
    yearProd: null,
    yearBudget: null,
    yearReforecast: null,
    consolidation: 'Annuelle',
    pays: "",
    bu: undefined,
    client: undefined,
    operation: undefined
};

const urlBaseApi = process.env.REACT_APP_BASE_API_URL as string;

const ExtractionReforecast = () => {
    const cookies = new Cookies();
    const token: string = cookies.get("jwt");

    const http: AxiosInstance = axios.create({
        baseURL: urlBaseApi,
        // timeout: 1000,
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    const [alertMessage, setAlerteMessage] = useState<string>('');
    const [openSnack, setOpenSnack] = useState<boolean>(false);
    const [alertType, setAlertType] = useState<AlertType>("success");

    const [loading, setLoading] = useState<boolean>(true);

    const [dataPays, setDataPays] = useState<DataRef[]>([]);
    const [dataBusinessUnits, setDataBusinessUnits] = useState<DataRef[]>([]);
    const [dataClients, setDataClients] = useState<DataRefClientWithBudget[]>([]);
    const [dataOperations, setDataOperations] = useState<DataRefOperationWithBudget[]>([]);

    const [extractionData, setExtractionData] = useState<ExtractData>(initialData);

    const [extractionType, setExtractionType] = useState<EXTRACTION_TYPE>(ExtractionType.REALISER_VS_BUDGET_VS_REFORECAST);

    const [errorInput, setErrorInput] = useState<string[]>([]);

    useEffect(() => {
        http.get('pivot-client-bu/get-all-exist-pays-in-association')
            .then(res => {
                Array.isArray(res.data) && setDataPays(res.data);
                setLoading(false);
            }).catch(error => {
                console.log(error);
            });
    }, []);

    useEffect(() => {
        setExtractionData({
            ...extractionData,
            bu: undefined,
            client: undefined,
            operation: undefined
        });
        setDataBusinessUnits([]);
        setDataClients([]);
        setDataOperations([]);

        if (extractionData?.pays) {
            // Récupère les clients du pays sélectionné
            http.get(`bu/findby-pays/${extractionData?.pays}`).then((res) => {
                Array.isArray(res.data) && setDataBusinessUnits(res.data);
            }).catch((error) => {
                console.log(error);
            });
        }
    }, [extractionData?.pays]);

    useEffect(() => {
        setExtractionData({
            ...extractionData,
            client: undefined,
            operation: undefined
        });
        setDataClients([]);
        setDataOperations([]);

        if (extractionData?.bu) {
            // Récupère les clients du pays sélectionné
            http.post("budget/get/client/prod-budget/by/model/pays/bu", {
                pays: extractionData?.pays,
                bu: extractionData?.bu
            }).then((res) => {
                Array.isArray(res.data) && setDataClients(res.data);
            }).catch((error) => {
                console.log(error);
            });
        }
    }, [extractionData?.bu]);

    useEffect(() => {
        setExtractionData({
            ...extractionData,
            operation: undefined
        });
        setDataOperations([]);

        if (extractionData?.client) {
            const splitedClientValue = extractionData?.client?.split('_');

            // Récupère les clients du pays sélectionné
            http.post('budget/get/operation/prod-budget/by/model/pays/bu/client', {
                pays: extractionData?.pays,
                bu: extractionData?.bu,
                is_client_budget: splitedClientValue[0] === MODE_BUDGET,
                client: Number(splitedClientValue[1])
            }).then((res) => {
                Array.isArray(res.data) && setDataOperations(res.data);
            }).catch((error) => {
                console.log(error);
            });
        }
    }, [extractionData?.client]);

    useEffect(() => {
        setExtractionData(initialData);
    }, [extractionType]);

    const handleChange = (e: any) => {
        const { name, value } = e.target;

        setExtractionData({
            ...extractionData,
            [name]: value
        });
    };

    const handleChangeYear = (e: Dayjs | null, name: string) => {
        setExtractionData({
            ...extractionData,
            [name]: e
        });
    };

    const isValidate = () => {
        // Reinitialise les variables des validations
        setErrorInput([]);

        let newErrorValues = [];

        if (extractionType === ExtractionType.REALISER_VS_REFORECAST) {
            !CheckAttr(extractionData?.yearProd) && newErrorValues.push('yearProd');
            !CheckAttr(extractionData?.yearReforecast) && newErrorValues.push('yearReforecast');
        } else if (extractionType === ExtractionType.BUDGET_VS_REFORECAST) {
            !CheckAttr(extractionData?.yearBudget) && newErrorValues.push('yearBudget');
            !CheckAttr(extractionData?.yearReforecast) && newErrorValues.push('yearReforecast');
        } else {
            !CheckAttr(extractionData?.yearProd) && newErrorValues.push('yearProd');
            !CheckAttr(extractionData?.yearBudget) && newErrorValues.push('yearBudget');
            !CheckAttr(extractionData?.yearReforecast) && newErrorValues.push('yearReforecast');
        }

        if (newErrorValues.length > 0) {
            setAlerteMessage("Merci de remplir les champs en rouge SVP!");
            setAlertType("warning");
            setOpenSnack(true);

            setErrorInput(newErrorValues);

            return false;
        }

        return true;
    }

    const getUrlExtraction = () => {
        switch (extractionType) {
            case ExtractionType.REALISER_VS_REFORECAST:
                return 'extraction/realised_vs_reforecast';
            case ExtractionType.BUDGET_VS_REFORECAST:
                return 'extraction/budget_vs_reforecast';
            default:
                return 'extraction/realised_vs_budget_vs_reforecast';
        }
    }

    const handleExtract = () => {
        if (isValidate()) {
            setLoading(true);

            // Valeur du client
            let is_client_budget;
            let client;
            if (extractionData?.client) {
                const splitedClientValue = extractionData?.client?.split('_');
                is_client_budget = splitedClientValue[0] === MODE_BUDGET;
                client = Number(splitedClientValue[1]);
            }

            // Valeur operation
            let is_operation_budget;
            let operation;
            if (extractionData?.operation) {
                const splitedOperationValue = extractionData?.operation?.split('_');
                is_operation_budget = splitedOperationValue[0] === MODE_BUDGET;
                operation = Number(splitedOperationValue[1]);
            }

            const data = {
                realised_year: extractionData?.yearProd?.format('YYYY'),
                budget_year: extractionData?.yearBudget?.format('YYYY'),
                reforecast_year: extractionData?.yearReforecast?.format('YYYY'),
                consolidation: extractionData?.consolidation,
                pays: CheckAttr(extractionData?.pays) && extractionData?.pays,
                bu: extractionData?.bu,
                is_client_budget,
                client,
                is_operation_budget,
                operation
            };

            const url = getUrlExtraction();

            http.post(url, data)
                .then(res => {
                    // On télécharge le fichier
                    const { filename } = res.data;
                    downloadFile(filename as string);

                    setAlerteMessage("Extraction efféctuée avec succès !");
                    setAlertType("success");
                    setOpenSnack(true);
                }).catch((error) => {
                    console.log(error);
                    setAlertType("error");
                    setOpenSnack(true);
                }).finally(() => {
                    setLoading(false);
                });
        }
    };

    const minDateOfBudgetYear = dayjs(new Date(new Date().getFullYear(), 0, 1));
    const maxDateOfBudgetYear = dayjs(new Date((new Date().getFullYear() + 1), 11, 31));

    const minDateOfRealisedYear = ((new Date().getFullYear() - 1) < 2024) ? minDateOfBudgetYear : dayjs(new Date((new Date().getFullYear() - 1), 0, 1));
    const maxDateOfRealisedYear = dayjs(new Date(new Date().getFullYear(), 11, 31));

    const handleCloseAlert = () => {
        setOpenSnack(false)
    }

    let vsInputElement = null;
    switch (extractionType) {
        case ExtractionType.REALISER_VS_REFORECAST:
            vsInputElement = <Fragment>
                <Input
                    label="Année realisé..."
                    name="yearProd"
                    value={extractionData?.yearProd}
                    columnSize={[6, 12, 5.5]}
                    minDate={minDateOfRealisedYear}
                    maxDate={maxDateOfRealisedYear}
                    errorInput={errorInput}
                    handleChange={handleChangeYear}
                />

                <VsElement />

                <Input
                    label="Année Reforecast..."
                    name="yearReforecast"
                    value={extractionData?.yearReforecast}
                    columnSize={[6, 12, 5.5]}
                    minDate={minDateOfBudgetYear}
                    maxDate={maxDateOfBudgetYear}
                    errorInput={errorInput}
                    handleChange={handleChangeYear}
                />
            </Fragment>
            break;
        case ExtractionType.BUDGET_VS_REFORECAST:
            vsInputElement = <Fragment>
                <Input
                    label="Année Budget..."
                    name="yearBudget"
                    value={extractionData?.yearBudget}
                    columnSize={[6, 12, 5.5]}
                    minDate={minDateOfBudgetYear}
                    maxDate={maxDateOfBudgetYear}
                    errorInput={errorInput}
                    handleChange={handleChangeYear}
                />

                <VsElement />

                <Input
                    label="Année Reforecast..."
                    name="yearReforecast"
                    value={extractionData?.yearReforecast}
                    columnSize={[6, 12, 5.5]}
                    minDate={minDateOfBudgetYear}
                    maxDate={maxDateOfBudgetYear}
                    errorInput={errorInput}
                    handleChange={handleChangeYear}
                />
            </Fragment>
            break;

        default:
            vsInputElement = <Fragment>
                <Input
                    label="Année realisé..."
                    name="yearProd"
                    value={extractionData?.yearProd}
                    columnSize={[6, 12, 3.33]}
                    minDate={minDateOfRealisedYear}
                    maxDate={maxDateOfRealisedYear}
                    errorInput={errorInput}
                    handleChange={handleChangeYear}
                />

                <VsElement />

                <Input
                    label="Année Budget..."
                    name="yearBudget"
                    value={extractionData?.yearBudget}
                    columnSize={[6, 12, 3.33]}
                    minDate={minDateOfBudgetYear}
                    maxDate={maxDateOfBudgetYear}
                    errorInput={errorInput}
                    handleChange={handleChangeYear}
                />

                <VsElement />

                <Input
                    label="Année Reforecast..."
                    name="yearReforecast"
                    value={extractionData?.yearReforecast}
                    columnSize={[6, 12, 3.33]}
                    minDate={minDateOfBudgetYear}
                    maxDate={maxDateOfBudgetYear}
                    errorInput={errorInput}
                    handleChange={handleChangeYear}
                />
            </Fragment>
            break;
    }

    const stackAlert: JSX.Element = SnackBarAlert(openSnack, handleCloseAlert, alertType, alertMessage);

    return (
        <>
            <Container maxWidth="md" sx={{ mt: 2 }}>
                <Card sx={{ maxWidth: 845 }}>
                    <CardMedia
                        component="img"
                        image="/images/extraction_reforecast.png"
                        alt="green iguana"
                        sx={{ display: { xs: 'none', sm: 'flex' }, height: 140 }}
                    />

                    <CardContent>
                        <Box>
                            <Grid container spacing={2} columns={{ xs: 6, sm: 12, md: 12 }} sx={{ mt: 1 }}>
                                <Grid item xs={6} sm={12} md={12}>
                                    <ThemeProvider theme={theme}>
                                        <FormControl>
                                            <FormLabel id="demo-radio-buttons-group-label" color="warning">Type :</FormLabel>
                                            <RadioGroup
                                                aria-labelledby="demo-radio-buttons-group-label"
                                                name="radio-buttons-group"
                                                value={extractionType}
                                                onChange={(e) => setExtractionType(e.target.value as EXTRACTION_TYPE)}
                                            >
                                                <FormControlLabel
                                                    value={ExtractionType.REALISER_VS_BUDGET_VS_REFORECAST}
                                                    control={<Radio size="small" color="warning" sx={{ ml: 4 }} />}
                                                    label="Realisé vs Budget vs Reforecast"
                                                />
                                                <FormControlLabel
                                                    value={ExtractionType.REALISER_VS_REFORECAST}
                                                    control={<Radio size="small" color="warning" sx={{ ml: 4 }} />}
                                                    label="Realisé vs Reforecast"
                                                />
                                                <FormControlLabel
                                                    value={ExtractionType.BUDGET_VS_REFORECAST}
                                                    control={<Radio size="small" color="warning" sx={{ ml: 4 }} />}
                                                    label="Budget vs Reforecast"
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    </ThemeProvider>
                                </Grid>
                            </Grid>

                            <Grid container spacing={2} columns={{ xs: 6, sm: 12, md: 12 }} sx={{ mt: 1 }}>
                                {vsInputElement}
                            </Grid>

                            <Grid container columns={{ xs: 6, sm: 12, md: 12 }} sx={{ mt: 3 }}>
                                <Grid item xs={6} sm={12} md={12}>
                                    <FormControl
                                        variant="outlined"
                                        fullWidth
                                        sx={{ marginTop: 1 }}
                                    >
                                        <InputLabel
                                            htmlFor="consolidation"
                                            color="warning"
                                            shrink={CheckAttr(extractionData?.consolidation)}
                                        >
                                            Consolidation
                                        </InputLabel>
                                        <Select
                                            id="consolidation"
                                            native
                                            color="warning"
                                            value={extractionData?.consolidation}
                                            error={errorInput.includes('consolidation')}
                                            onChange={handleChange}
                                            label="Consolidation"
                                            inputProps={{
                                                name: "consolidation",
                                            }}
                                        >
                                            <option value="Annuelle">Annuelle</option>
                                            <option value="Mensuelle">Mensuelle</option>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <Divider sx={{ mt: 5, mb: 1 }}>Filtre</Divider>

                            <Grid container spacing={2} columns={{ xs: 6, sm: 12, md: 12 }}>
                                <Grid item xs={6} sm={6} md={6}>
                                    <FormControl
                                        fullWidth
                                        variant="outlined"
                                        sx={{ marginTop: 1 }}
                                    >
                                        <InputLabel
                                            htmlFor="pays"
                                            color="warning"
                                            shrink={CheckAttr(extractionData?.pays)}
                                        >
                                            Pays de production
                                        </InputLabel>
                                        <Select
                                            id="pays"
                                            native
                                            color="warning"
                                            value={extractionData?.pays}
                                            onChange={handleChange}
                                            label="Pays de production"
                                            inputProps={{
                                                name: "pays",
                                            }}
                                        >
                                            <option value=""></option>
                                            {dataPays.map((pays) => (
                                                <option key={pays?.id} value={pays?.id}>
                                                    {pays?.libelle}
                                                </option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={6} md={6}>
                                    <FormControl
                                        fullWidth
                                        variant="outlined"
                                        sx={{ marginTop: 1 }}
                                    >
                                        <InputLabel
                                            htmlFor="bu"
                                            color="warning"
                                            shrink={CheckAttr(extractionData?.bu)}
                                        >
                                            Business Unit
                                        </InputLabel>
                                        <Select
                                            id="bu"
                                            native
                                            color="warning"
                                            value={extractionData?.bu}
                                            onChange={handleChange}
                                            label="Business Unit"
                                            inputProps={{
                                                name: "bu",
                                            }}
                                        >
                                            <option value=""></option>
                                            {dataBusinessUnits.map((bu) => (
                                                <option key={bu?.id} value={bu?.id}>
                                                    {bu?.libelle}
                                                </option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={6} md={6}>
                                    <FormControl
                                        fullWidth
                                        variant="outlined"
                                        sx={{ marginTop: 1 }}
                                    >
                                        <InputLabel
                                            htmlFor="client"
                                            color="warning"
                                            shrink={CheckAttr(extractionData?.client)}
                                        >
                                            Client
                                        </InputLabel>
                                        <Select
                                            id="client"
                                            native
                                            color="warning"
                                            value={extractionData?.client}
                                            onChange={handleChange}
                                            label="Client"
                                            inputProps={{
                                                name: "client",
                                            }}
                                        >
                                            <option value={undefined}></option>
                                            {dataClients.map((client) => (
                                                <option key={client?.id} value={(client?.is_clientBudget === 1) ? `budget_${client?.id}` : `prod_${client?.id}`}>
                                                    {client?.libelle}
                                                </option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={6} md={6}>
                                    <FormControl
                                        fullWidth
                                        variant="outlined"
                                        sx={{ marginTop: 1 }}
                                    >
                                        <InputLabel
                                            htmlFor="operation"
                                            color="warning"
                                            shrink={CheckAttr(extractionData?.operation)}
                                        >
                                            Opération
                                        </InputLabel>
                                        <Select
                                            id="operation"
                                            native
                                            color="warning"
                                            value={extractionData?.operation}
                                            onChange={handleChange}
                                            label="Opération"
                                            inputProps={{
                                                name: "operation",
                                            }}
                                        >
                                            <option value={undefined}></option>
                                            {dataOperations.map((operation) => (
                                                <option key={operation?.id} value={(operation?.is_operationBudget === 1) ? `budget_${operation?.id}` : `prod_${operation?.id}`}>
                                                    {operation?.libelle}
                                                </option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>
                    </CardContent>

                    <CardActions sx={{ mt: 5, mb: 3 }}>
                        <Grid container spacing={2} columns={{ xs: 12, sm: 12, md: 12 }}>
                            <Grid item xs={12} sm={12} md={12}>
                                <Box display="flex" justifyContent="center">
                                    <Button
                                        variant="contained"
                                        color="warning"
                                        size="large"
                                        sx={{ textTransform: 'none' }}
                                        endIcon={<FileDownloadIcon />}
                                        onClick={() => handleExtract()}
                                    >
                                        Extraire
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardActions>
                </Card>
            </Container>

            {stackAlert}
            <div>
                <Backdrop
                    sx={{ color: "white", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={loading}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
            </div>
        </>
    );
};

export default ExtractionReforecast;